import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit3, Trash2, Save, X, BarChart3, FileText, MessageSquare, LogOut, GripVertical, Volume2, Upload, Settings, Send, UserPlus, Lock, Play, Pause, ListOrdered, GitBranch } from 'lucide-react';
import { getAllQuestions, createQuestion, updateQuestion, deleteQuestion, reorderQuestions, API_BASE_URL } from '../api';
import AudioRecorder from '../components/AudioRecorder';
import toast from 'react-hot-toast';

const AdminQuestions = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    section: '', section_order: 1, question_order: 1,
    question_text: '', question_type: 'single_choice',
    options: '', is_required: 1, is_active: 1, skip_logic: ''
  });
  const [reordering, setReordering] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [existingAudioUrl, setExistingAudioUrl] = useState(null);
  const [optionsList, setOptionsList] = useState([]);
  const [optionsAudioFiles, setOptionsAudioFiles] = useState([]);
  const [existingOptionsAudio, setExistingOptionsAudio] = useState([]);
  const [playingQuestionAudio, setPlayingQuestionAudio] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (user.role === 'agent') { navigate('/agent'); return; }
    loadQuestions();
  }, [navigate]);

  const loadQuestions = async () => {
    try {
      const res = await getAllQuestions();
      setQuestions(res.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/admin'); }
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!formData.question_text || !formData.section) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key]);
    });
    if (audioFile) data.append('audio', audioFile);
    if (editingId && !existingAudioUrl) {
      data.append('remove_audio', 'true');
    }
    if (editingId && existingOptionsAudio.length > 0) {
      data.append('existing_options_audio', JSON.stringify(existingOptionsAudio));
    }
    optionsAudioFiles.forEach((file, idx) => {
      if (file) data.append(`option_audio_${idx}`, file);
    });

    try {
      if (editingId) {
        await updateQuestion(editingId, data);
        toast.success('Question modifiee');
      } else {
        await createQuestion(data);
        toast.success('Question ajoutee');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadQuestions();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (q) => {
    setEditingId(q.id);
    setFormData({
      section: q.section,
      section_order: q.section_order,
      question_order: q.question_order,
      question_text: q.question_text,
      question_type: q.question_type,
      options: q.options ? (typeof q.options === 'string' ? q.options : JSON.stringify(q.options)) : '',
      is_required: q.is_required,
      is_active: q.is_active,
      skip_logic: q.skip_logic ? (typeof q.skip_logic === 'string' ? q.skip_logic : JSON.stringify(q.skip_logic)) : ''
    });
    setExistingAudioUrl(q.audio_url || null);
    setAudioFile(null);

    // Parse options for display
    let opts = [];
    try {
      const parsed = typeof q.options === 'string' ? JSON.parse(q.options) : q.options;
      if (Array.isArray(parsed)) opts = parsed;
      else if (parsed && parsed.domains) opts = parsed.domains;
    } catch (e) { opts = []; }
    setOptionsList(opts);

    // Parse existing option audios
    let optAudios = [];
    try {
      optAudios = q.options_audio ? (typeof q.options_audio === 'string' ? JSON.parse(q.options_audio) : q.options_audio) : [];
    } catch (e) { optAudios = []; }
    setExistingOptionsAudio(Array.isArray(optAudios) ? optAudios : []);
    setOptionsAudioFiles(new Array(opts.length).fill(null));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette question ?')) return;
    try {
      await deleteQuestion(id);
      toast.success('Question supprimee');
      loadQuestions();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ section: '', section_order: 1, question_order: 1, question_text: '', question_type: 'single_choice', options: '', is_required: 1, is_active: 1, skip_logic: '' });
    setAudioFile(null);
    setExistingAudioUrl(null);
    setOptionsList([]);
    setOptionsAudioFiles([]);
    setExistingOptionsAudio([]);
    setPlayingQuestionAudio(false);
  };

  const handleReorder = async () => {
    if (!window.confirm('Renuméroter toutes les questions séquentiellement (1, 2, 3...) selon leur ordre actuel ?')) return;
    setReordering(true);
    try {
      const res = await reorderQuestions();
      toast.success(`${res.data.reordered} questions réordonnées`);
      loadQuestions();
    } catch (err) {
      toast.error('Erreur lors du réordonnancement');
    }
    setReordering(false);
  };

  const handleLogout = () => { localStorage.clear(); navigate('/admin'); };

  const groupedQuestions = questions.reduce((acc, q) => {
    if (!acc[q.section]) acc[q.section] = [];
    acc[q.section].push(q);
    return acc;
  }, {});

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7f5' }}>
      {/* Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '240px', background: 'linear-gradient(180deg, #1B5E20, #2E7D32)', padding: '24px 16px', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', padding: '0 8px' }}>
          <img src="/logo.webp" alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', padding: '2px' }} />
          <div>
            <div style={{ color: '#FFD54F', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px' }}>ADMIN</div>
            <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>Khombole</div>
          </div>
        </div>
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {[
            { icon: <BarChart3 size={18} />, label: 'Tableau de bord', path: '/admin/dashboard' },
            { icon: <FileText size={18} />, label: 'Questions', path: '/admin/questions', active: true },
            { icon: <MessageSquare size={18} />, label: 'Reponses', path: '/admin/responses' },
            { icon: <UserPlus size={18} />, label: 'Agents', path: '/admin/agents' },
            { icon: <Settings size={18} />, label: 'Paramètres', path: '/admin/settings' },
            { icon: <Lock size={18} />, label: 'Mon Compte', path: '/admin/profile' },
          ].map((item, i) => (
            <button key={i} onClick={() => navigate(item.path)}
              style={{ background: item.active ? 'rgba(255,255,255,0.15)' : 'transparent', border: 'none', color: 'white', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: item.active ? '600' : '400', textAlign: 'left', width: '100%' }}>
              {item.icon} {item.label}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.7)', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
          <LogOut size={18} /> Deconnexion
        </button>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '240px', padding: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', fontFamily: 'Playfair Display, serif' }}>Gestion des Questions</h1>
            <p style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>{questions.length} questions au total</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleReorder} disabled={reordering}
              style={{ background: 'white', color: '#1565C0', border: '1.5px solid #1565C0', padding: '12px 18px', borderRadius: '10px', cursor: reordering ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', opacity: reordering ? 0.6 : 1 }}
              title="Renuméroter les questions séquentiellement">
              <ListOrdered size={16} /> {reordering ? 'En cours...' : 'Réordonner'}
            </button>
            <button onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }}
              style={{ background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600' }}>
              <Plus size={16} /> Ajouter une question
            </button>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '24px' }}>
            <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700' }}>{editingId ? 'Modifier la question' : 'Nouvelle question'}</h3>
                <button onClick={() => { setShowForm(false); setEditingId(null); }} style={{ background: '#f5f5f5', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={18} />
                </button>
              </div>

              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Section *</label>
                    <input value={formData.section} onChange={(e) => setFormData({ ...formData, section: e.target.value })} placeholder="Ex: PROFIL DU REPONDANT"
                      style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Ordre section</label>
                      <input type="number" value={formData.section_order} onChange={(e) => setFormData({ ...formData, section_order: parseInt(e.target.value) })}
                        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Ordre question</label>
                      <input type="number" value={formData.question_order} onChange={(e) => setFormData({ ...formData, question_order: parseInt(e.target.value) })}
                        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Texte de la question *</label>
                  <textarea value={formData.question_text} onChange={(e) => setFormData({ ...formData, question_text: e.target.value })} rows={3}
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'Inter, sans-serif' }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Type de question</label>
                    <select value={formData.question_type} onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', outline: 'none', background: 'white' }}>
                      <option value="single_choice">Choix unique</option>
                      <option value="multiple_choice">Choix multiple</option>
                      <option value="text">Texte libre</option>
                      <option value="rating_matrix">Matrice de notation</option>
                      <option value="ranking">Classement</option>
                      <option value="contact">Contact</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', paddingBottom: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={formData.is_required === 1} onChange={(e) => setFormData({ ...formData, is_required: e.target.checked ? 1 : 0 })} />
                      Obligatoire
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={formData.is_active === 1} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })} />
                      Active
                    </label>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Options (JSON)</label>
                  <textarea
                    value={formData.options}
                    onChange={(e) => {
                      setFormData({ ...formData, options: e.target.value });
                      try {
                        const parsed = JSON.parse(e.target.value);
                        if (Array.isArray(parsed)) {
                          setOptionsList(parsed);
                          setOptionsAudioFiles(new Array(parsed.length).fill(null));
                          setExistingOptionsAudio(prev => {
                            const merged = new Array(parsed.length).fill(null);
                            prev.forEach((v, i) => { if (i < parsed.length) merged[i] = v; });
                            return merged;
                          });
                        }
                      } catch (err) { /* invalid JSON */ }
                    }}
                    rows={3} placeholder='["Option 1", "Option 2", "Option 3"]'
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '12px', outline: 'none', resize: 'vertical', fontFamily: 'monospace' }} />
                  <p style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>Format JSON. Pour texte libre, laisser vide.</p>
                </div>

                {/* Audio par réponse */}
                {optionsList.length > 0 && ['single_choice', 'multiple_choice', 'ranking'].includes(formData.question_type) && (
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
                      <Volume2 size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                      Audio par réponse (optionnel)
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {optionsList.map((opt, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#fafafa', borderRadius: '8px', border: '1px solid #eee' }}>
                          <div style={{ fontSize: '12px', color: '#666', minWidth: '24px', fontWeight: '600' }}>{idx + 1}.</div>
                          <div style={{ flex: 1, fontSize: '13px', color: '#333' }}>{opt}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AudioRecorder
                              existingUrl={existingOptionsAudio[idx] ? `${API_BASE_URL}${existingOptionsAudio[idx]}` : null}
                              onRecord={(file) => {
                                const updated = [...optionsAudioFiles];
                                updated[idx] = file;
                                setOptionsAudioFiles(updated);
                              }}
                              onClear={() => {
                                const updated = [...optionsAudioFiles];
                                updated[idx] = null;
                                setOptionsAudioFiles(updated);
                              }}
                              onDeleteExisting={() => {
                                const updated = [...existingOptionsAudio];
                                updated[idx] = null;
                                setExistingOptionsAudio(updated);
                              }}
                            />
                            {optionsAudioFiles[idx] && !optionsAudioFiles[idx].name?.startsWith('recorded_') && (
                              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#2E7D32', fontWeight: '500' }}>
                                <Upload size={12} />
                                {optionsAudioFiles[idx].name.substring(0, 12)}...
                                <input type="file" accept="audio/*" style={{ display: 'none' }}
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      const updated = [...optionsAudioFiles];
                                      updated[idx] = e.target.files[0];
                                      setOptionsAudioFiles(updated);
                                    }
                                  }} />
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Logique de saut conditionnel */}
                {optionsList.length > 0 && formData.question_type === 'single_choice' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
                      <GitBranch size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                      Saut conditionnel (optionnel)
                    </label>
                    <p style={{ fontSize: '11px', color: '#999', marginBottom: '10px' }}>Pour chaque réponse, indiquez le numéro de question vers lequel sauter (laisser vide = question suivante).</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {optionsList.map((opt, idx) => {
                        let skipObj = {};
                        try { skipObj = formData.skip_logic ? JSON.parse(formData.skip_logic) : {}; } catch (e) { skipObj = {}; }
                        const current = skipObj[opt] ? (typeof skipObj[opt] === 'object' ? skipObj[opt].skip_to_order : skipObj[opt]) : '';
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#fafafa', borderRadius: '8px', border: '1px solid #eee' }}>
                            <span style={{ flex: 1, fontSize: '13px', color: '#333' }}>{opt}</span>
                            <span style={{ fontSize: '11px', color: '#999' }}>→ Q</span>
                            <input type="number" min="1" value={current}
                              onChange={(e) => {
                                let obj = {};
                                try { obj = formData.skip_logic ? JSON.parse(formData.skip_logic) : {}; } catch (er) { obj = {}; }
                                const val = e.target.value;
                                if (val === '' || val === null) { delete obj[opt]; }
                                else { obj[opt] = { skip_to_order: Number(val) }; }
                                setFormData({ ...formData, skip_logic: Object.keys(obj).length ? JSON.stringify(obj) : '' });
                              }}
                              style={{ width: '70px', padding: '6px 8px', border: '1.5px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                              placeholder="suiv." />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Audio de la question (optionnel)</label>
                  <AudioRecorder
                    existingUrl={existingAudioUrl ? `${API_BASE_URL}${existingAudioUrl}` : null}
                    onRecord={(file) => setAudioFile(file)}
                    onClear={() => setAudioFile(null)}
                    onDeleteExisting={() => setExistingAudioUrl(null)}
                  />
                  {audioFile && !audioFile.name?.startsWith('recorded_') && (
                    <div style={{ border: '1.5px dashed #e0e0e0', borderRadius: '8px', padding: '12px', textAlign: 'center', marginTop: '8px' }}>
                      <input type="file" accept="audio/*" onChange={(e) => setAudioFile(e.target.files[0])} style={{ display: 'none' }} id="audio-upload" />
                      <label htmlFor="audio-upload" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#666', fontSize: '12px' }}>
                        <Upload size={14} /> {audioFile.name}
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowForm(false); setEditingId(null); }}
                  style={{ padding: '10px 20px', border: '1.5px solid #e0e0e0', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '13px' }}>
                  Annuler
                </button>
                <button onClick={handleSave}
                  style={{ padding: '10px 20px', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Save size={14} /> {editingId ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions List */}
        {Object.entries(groupedQuestions).map(([section, sectionQuestions]) => (
          <div key={section} style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#2E7D32', marginBottom: '12px', padding: '8px 16px', background: '#E8F5E9', borderRadius: '8px', display: 'inline-block' }}>
              {section}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sectionQuestions.map((q) => (
                <div key={q.id} style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 8px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '12px', opacity: q.is_active ? 1 : 0.5 }}>
                  <GripVertical size={16} color="#ccc" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                      Q{q.question_order}. {q.question_text}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', background: '#E8F5E9', color: '#2E7D32', borderRadius: '4px' }}>{q.question_type}</span>
                      {q.audio_url && <span style={{ fontSize: '11px', padding: '2px 8px', background: '#FFF8E1', color: '#FF8F00', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}><Volume2 size={10} /> Q Audio</span>}
                      {(() => {
                        try {
                          const oa = q.options_audio ? (typeof q.options_audio === 'string' ? JSON.parse(q.options_audio) : q.options_audio) : [];
                          if (Array.isArray(oa) && oa.some(a => a)) return <span style={{ fontSize: '11px', padding: '2px 8px', background: '#E3F2FD', color: '#1565C0', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}><Volume2 size={10} /> R Audio</span>;
                        } catch (e) {}
                        return null;
                      })()}
                      {(() => {
                        try {
                          const sl = q.skip_logic ? (typeof q.skip_logic === 'string' ? JSON.parse(q.skip_logic) : q.skip_logic) : null;
                          if (sl && Object.keys(sl).length > 0) return <span style={{ fontSize: '11px', padding: '2px 8px', background: '#EDE7F6', color: '#5E35B1', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}><GitBranch size={10} /> Saut</span>;
                        } catch (e) {}
                        return null;
                      })()}
                      {!q.is_active && <span style={{ fontSize: '11px', padding: '2px 8px', background: '#ffebee', color: '#E53935', borderRadius: '4px' }}>Inactive</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => handleEdit(q)} style={{ background: '#f5f5f5', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer' }}>
                      <Edit3 size={14} color="#666" />
                    </button>
                    <button onClick={() => handleDelete(q.id)} style={{ background: '#ffebee', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer' }}>
                      <Trash2 size={14} color="#E53935" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminQuestions;
